/**
 * Pick ONE customer, by typing.
 *
 * WHY THIS EXISTS, and it is not only ergonomics. The new-agreement form used a
 * plain `<select>` fed by `GET /api/customers` with no `q` and no `limit` — and
 * that endpoint defaults to **limit=50**. So the list was the first fifty
 * customers alphabetically, with no indication that anything followed. A
 * customer whose name sorts after the fiftieth could not be chosen at all, and
 * the screen gave no hint of it. Jeff hit it on staging with real data.
 *
 * SERVER-SIDE SEARCH, NOT A BROWSER FILTER, for exactly the reason
 * `CustomerPicker` gives for the same decision: the customer base is more than
 * one page, so filtering the fetched page in the browser looks like search and
 * silently searches a fraction. That is the same class of lie as a sort header
 * that only orders the rows you happen to hold.
 *
 * The bounded sibling is `FilterableSelect`, used for a customer's own
 * properties — that list IS complete in the browser, so filtering locally is
 * honest there. The two components differ because the data differs, and each
 * says so.
 *
 * NOT `CustomerPicker`. That one is a multi-select FILTER: it owns chips, a
 * `max`, and URL round-tripping for the Agreements tab. Bending it to single
 * select would mean a `max: 1` mode whose chips, clear-all and "N selected"
 * copy all have to be special-cased — more branching than a focused component,
 * on a component two surfaces already depend on.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import {
  DropdownPanel,
  isInDropdown,
} from '@/features/crm/components/DropdownPanel';

/** How many matches to show. Enough to choose from, short enough to scan. */
const PAGE = 25;

/** Keystroke settle time before a request. Matches CustomerPicker. */
const DEBOUNCE_MS = 250;

interface CustomerRow {
  customer_id: string;
  name: string | null;
  city?: string | null;
  state?: string | null;
}

interface CustomerSearchResult {
  customers: CustomerRow[];
  total?: number;
  truncated?: boolean;
}

export interface CustomerSearchSelectProps {
  /** The chosen customer id, or '' for none. */
  value: string;
  onChange: (customerId: string) => void;
  /**
   * The chosen customer's name, when the caller knows it. Lets the closed
   * control read as a name rather than an id after a reload, without this
   * component having to fetch a single customer to label itself.
   */
  valueName?: string | null;
  id?: string;
  testId?: string;
}

export function CustomerSearchSelect({
  value,
  onChange,
  valueName,
  id,
  testId = 'customer-search-select',
}: CustomerSearchSelectProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<CustomerRow[]>([]);
  const [total, setTotal] = useState(0);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Names seen in any result, so a selection keeps its label after a re-search. */
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});
  const boxRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  /*
   * Debounced, and only while open — a closed control firing a customer search
   * on every keystroke of the form's other twenty fields is pure waste.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      const term = q.trim();
      const url = term
        ? `/api/customers?limit=${PAGE}&q=${encodeURIComponent(term)}`
        : `/api/customers?limit=${PAGE}`;
      apiFetch<CustomerSearchResult>(url)
        .then((d) => {
          if (cancelled) return;
          const list = d.customers ?? [];
          setRows(list);
          setTotal(d.total ?? list.length);
          /*
           * Never default to "not truncated". Presenting a page as the whole
           * customer base is the bug this component was written to fix, so the
           * fallback errs the other way.
           */
          setCapped(d.truncated ?? (d.total ?? 0) > list.length);
          setKnownNames((prev) => {
            const next = { ...prev };
            for (const c of list) if (c.name) next[c.customer_id] = c.name;
            return next;
          });
        })
        .catch((e) => {
          if (cancelled) return;
          // An ERROR, not an empty list. "No customers match" is a claim about
          // the data; a failed request is a claim about us, and the two must not
          // look the same on a form someone is trying to submit.
          setError(e instanceof ApiError ? e.message : String(e));
          setRows([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q, open]);

  /*
   * Close on an outside click. Without it the panel sits over the fields below
   * and swallows the next click the user makes.
   *
   * THE PANEL IS NOT A DESCENDANT of this box — it is portaled to the body to
   * escape the card's `overflow: hidden` — so `contains` alone reads a click on
   * an OPTION as a click outside, closes the panel on mousedown, and the click
   * never becomes a selection. `isInDropdown` is the other half of the test.
   */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const inBox = boxRef.current?.contains(e.target as Node) ?? false;
      if (!inBox && !isInDropdown(e.target, `${testId}-panel`)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, testId]);

  const label = value ? (knownNames[value] ?? valueName ?? value) : '';

  return (
    /*
     * `data-value` carries the CHOSEN ID, and it is not only for tests.
     *
     * The visible input shows a NAME, and that name can lag the id: the
     * document-extraction flow sets a customer programmatically, and the label
     * only resolves once that customer appears in a search result or the
     * caller supplies `valueName`. So the id is the state and the label is a
     * rendering of it — exposing the id keeps the DOM honest about which of the
     * two the form will actually submit.
     */
    <div
      data-testid={testId}
      data-value={value}
      ref={boxRef}
      style={{ position: 'relative' }}
    >
      <input
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        autoComplete="off"
        className="input"
        data-testid={`${testId}-input`}
        id={id}
        onChange={(e) => {
          setQ(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={
          value ? label : 'Type to search customers — name or account number'
        }
        role="combobox"
        /*
         * The input shows the SEARCH TERM while open and the CHOSEN NAME while
         * closed. Keeping the chosen name in the box while typing would mean
         * clearing it on first keystroke, which loses the selection if the user
         * changes their mind and clicks away.
         */
        value={open ? q : label}
      />

      {value && !open && (
        <button
          className="btn btn-sm"
          data-testid={`${testId}-clear`}
          onClick={() => {
            onChange('');
            setQ('');
          }}
          style={{ position: 'absolute', right: 4, top: 4 }}
          type="button"
        >
          Clear
        </button>
      )}

      {/*
       * PORTALED, not absolutely positioned inside this box.
       *
       * `.crm-scope .section` sets `overflow: hidden` — required, because the
       * full-bleed card tables rely on it to clip their rounded corners — so a
       * panel positioned inside the card was CLIPPED at the card's border.
       * That is the "gets cut off when looking for the customer" Jeff reported
       * on 2026-09-10, and `full-bleed-corner-guard.spec.ts` had already named
       * this as the reason someone would wrongly delete that overflow rule.
       * See DropdownPanel for the whole story.
       */}
      {open && (
        <DropdownPanel
          anchor={boxRef.current}
          id={listId}
          role="listbox"
          testId={`${testId}-panel`}
        >
          {loading && rows.length === 0 && (
            <p className="detail-note" style={{ margin: 8 }}>
              <span className="muted">Searching…</span>
            </p>
          )}

          {error && (
            <p
              className="detail-note"
              data-testid={`${testId}-error`}
              style={{ margin: 8 }}
            >
              <span className="muted">
                Customer search isn&rsquo;t available right now — this is a
                connection problem, not an empty customer list.
              </span>
            </p>
          )}

          {!loading && !error && rows.length === 0 && (
            <p
              className="detail-note"
              data-testid={`${testId}-empty`}
              style={{ margin: 8 }}
            >
              <span className="muted">
                No customer matches &ldquo;{q.trim()}&rdquo;. Try part of the
                name, or the account number.
              </span>
            </p>
          )}

          {rows.map((c) => (
            <button
              aria-selected={c.customer_id === value}
              className="btn btn-sm"
              data-testid={`${testId}-option-${c.customer_id}`}
              key={c.customer_id}
              onClick={() => {
                onChange(c.customer_id);
                setQ('');
                setOpen(false);
              }}
              role="option"
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                borderRadius: 0,
              }}
              type="button"
            >
              {c.name ?? c.customer_id}{' '}
              <span className="muted" style={{ fontSize: 11 }}>
                {c.customer_id}
                {c.city ? ` · ${c.city}${c.state ? `, ${c.state}` : ''}` : ''}
              </span>
            </button>
          ))}

          {/*
           * SAYS SO WHEN THE LIST IS A PAGE. This is the whole point of the
           * component: the old control showed fifty of several hundred and
           * claimed nothing. A count with an instruction is the difference
           * between a truncated list and a lie.
           */}
          {capped && rows.length > 0 && (
            <p
              className="detail-note"
              data-testid={`${testId}-capped`}
              style={{ margin: 8, fontSize: 11 }}
            >
              <span className="muted">
                Showing {rows.length.toLocaleString()} of{' '}
                {total.toLocaleString()} — keep typing to narrow it.
              </span>
            </p>
          )}
        </DropdownPanel>
      )}
    </div>
  );
}
